import type { PromptResult, Recommendation, AnalysisMetrics } from '@visiward/schemas';
import { PROMPT_CATEGORY_LABELS } from '@visiward/shared';

/**
 * Derive practical, deterministic recommendations from prompt results and
 * aggregate metrics. This is heuristic (not model-generated) so it is stable,
 * cheap, and safe. Model-suggested `recommendations` on individual prompt
 * results are folded in as supporting evidence.
 */
export function generateRecommendations(
  results: PromptResult[],
  metrics: AnalysisMetrics,
): Recommendation[] {
  const recs: Recommendation[] = [];
  const total = results.length;
  if (total === 0) return recs;

  // 1) Comparison-query gap: competitors recommended where brand is not.
  const comparisonPrompts = results.filter((r) => r.category === 'comparison');
  const comparisonMisses = comparisonPrompts.filter((r) => !r.recommended);
  if (comparisonPrompts.length > 0 && comparisonMisses.length / comparisonPrompts.length >= 0.4) {
    recs.push({
      id: 'comparison-page',
      title: 'Create a competitor comparison page',
      priority: comparisonMisses.length >= 3 ? 'high' : 'medium',
      reason: 'Competitors are consistently recommended for comparison queries.',
      affectedPrompts: comparisonMisses.length,
      recommendedAction:
        'Add a structured comparison page with feature tables, ideal-user guidance, and FAQs.',
      expectedVisibilityArea: 'Comparison & high commercial intent queries',
      status: 'open',
    });
  }

  // 2) Low mention rate: the brand is largely absent.
  if (metrics.mentionRate < 40) {
    const missed = results.filter((r) => !r.brandMentioned).length;
    recs.push({
      id: 'foundational-visibility',
      title: 'Strengthen foundational brand presence',
      priority: metrics.mentionRate < 20 ? 'high' : 'medium',
      reason: 'The brand is not mentioned in a large share of relevant answers.',
      affectedPrompts: missed,
      recommendedAction:
        'Publish authoritative, well-structured pages describing your product, use cases, and category, and ensure schema.org Organization/Product markup is present.',
      expectedVisibilityArea: 'Discovery & category recommendation queries',
      status: 'open',
    });
  }

  // 3) Mentioned but not recommended.
  const mentionedNotRecommended = results.filter((r) => r.brandMentioned && !r.recommended);
  if (mentionedNotRecommended.length >= Math.max(2, Math.ceil(total * 0.2))) {
    recs.push({
      id: 'earn-recommendations',
      title: 'Turn mentions into recommendations',
      priority: 'medium',
      reason:
        'The brand is mentioned but not actively recommended, suggesting weak differentiation signals.',
      affectedPrompts: mentionedNotRecommended.length,
      recommendedAction:
        'Publish outcome-focused case studies, third-party reviews, and clear positioning that answers "why choose us".',
      expectedVisibilityArea: 'Category recommendation & problem-solving queries',
      status: 'open',
    });
  }

  // 4) Weak ranking despite mentions.
  if (metrics.top3InclusionRate < 30 && metrics.mentionRate >= 40) {
    recs.push({
      id: 'improve-ranking',
      title: 'Improve ranking within answers',
      priority: 'medium',
      reason: 'The brand appears but rarely in the top three of ranked answers.',
      affectedPrompts: results.filter((r) => r.rankPosition != null && r.rankPosition > 3).length,
      recommendedAction:
        'Build topical authority with in-depth guides, earn high-quality citations, and keep key facts consistent across the web.',
      expectedVisibilityArea: 'Ranked recommendation queries',
      status: 'open',
    });
  }

  // 5) Accuracy issues surfaced by the analysis.
  const withIssues = results.filter((r) => r.issues.length > 0);
  if (metrics.accuracyScore < 70 && withIssues.length > 0) {
    recs.push({
      id: 'correct-inaccuracies',
      title: 'Correct inaccurate brand information',
      priority: metrics.accuracyScore < 50 ? 'high' : 'medium',
      reason: 'Answers contain inaccuracies or outdated details about the brand.',
      affectedPrompts: withIssues.length,
      recommendedAction:
        'Publish a clear, canonical facts page, update structured data, and correct third-party listings and profiles.',
      expectedVisibilityArea: 'Branded audit queries',
      status: 'open',
    });
  }

  // 6) Category-specific gaps.
  const byCategoryMiss = new Map<string, number>();
  for (const r of results) {
    if (!r.brandMentioned) {
      byCategoryMiss.set(r.category, (byCategoryMiss.get(r.category) ?? 0) + 1);
    }
  }
  const worstCategory = Array.from(byCategoryMiss.entries()).sort((a, b) => b[1] - a[1])[0];
  if (worstCategory && worstCategory[1] >= 2 && recs.length < 6) {
    const label = PROMPT_CATEGORY_LABELS[worstCategory[0] as keyof typeof PROMPT_CATEGORY_LABELS];
    recs.push({
      id: `category-gap-${worstCategory[0]}`,
      title: `Close the ${label.toLowerCase()} content gap`,
      priority: 'low',
      reason: `The brand is frequently absent from ${label.toLowerCase()} answers.`,
      affectedPrompts: worstCategory[1],
      recommendedAction: `Create targeted content that directly addresses ${label.toLowerCase()} intent for your category.`,
      expectedVisibilityArea: `${label} queries`,
      status: 'open',
    });
  }

  return recs.slice(0, 6);
}
